import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Product } from '@models/product';
import { ClinicProductsService } from '@services/clinic-products.service';
import { ProductsService } from '@services/products.service';
import { Subject, combineLatest } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ProductBrandService } from '@services/product-brand.service';
import { ProductBrand } from '@models/product-brand';

@Component({
  selector: 'app-add-product',
  templateUrl: './add-product.component.html',
  styleUrls: ['./add-product.component.less'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ height: 0, opacity: 0 }))]),
    ]),

    trigger('fadeInItem', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-5px)' }),
        animate('250ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class AddProductComponent implements OnInit, OnDestroy {
  unsub: Subject<void> = new Subject<void>();

  loading = false;

  productsForm = this.formBuilder.group({
    search: this.formBuilder.control(''),
    brandGroups: this.formBuilder.array([]),
  });

  showAddBrand = false;
  newBrandName = '';
  showAddProduct: boolean[] = [];
  newProductNames: string[] = [];

  get brandGroups() {
    return this.productsForm.controls['brandGroups'] as FormArray;
  }

  get searchControl() {
    return this.productsForm.controls['search'] as FormControl;
  }

  constructor(
    public dialogRef: MatDialogRef<AddProductComponent>,
    private productsService: ProductsService,
    private clinicProductsService: ClinicProductsService,
    private formBuilder: FormBuilder,
    private productBrandService: ProductBrandService,
    private productService: ProductsService
  ) {}

  errorMessage: string;

  ngOnInit() {
    this.loading = true;
    this.showAddProduct = this.brandGroups.controls.map(() => false);
    this.newProductNames = this.brandGroups.controls.map(() => '');
    combineLatest([
      this.productsService.getProducts(),
      this.clinicProductsService.getProducts(),
      this.clinicProductsService.getListsForNewProduct().pipe(
        map((lists) => {
          lists.productBrands.push({ name: 'No Brand', id: 0, clinicProducts: [] });
          return lists;
        })
      ),
      this.searchControl.valueChanges.pipe(startWith('')),
    ])
      .pipe(takeUntil(this.unsub))
      .subscribe(([products, clinicProducts, newProductData, searchString]) => {
        const filteredProducts = this.filterProducts(products, searchString);
        this.brandGroups.clear();
        for (let productBrand of newProductData.productBrands) {
          const brandProducts = filteredProducts.filter((product) => product.productBrandId === productBrand.id);
          const productControls = brandProducts.map((product) => {
            const addDisabled = clinicProducts.some((cp) => cp.productId == product.id);
            return this.formBuilder.group({
              id: this.formBuilder.control(product.id),
              name: this.formBuilder.control(product.name),
              selected: this.formBuilder.control({ value: false, disabled: addDisabled }),
            });
          });

          const groupToAdd = this.formBuilder.group({
            brandId: this.formBuilder.control(productBrand.id),
            brandName: this.formBuilder.control({ value: productBrand.name, disabled: true }),
            products: this.formBuilder.array(productControls),
          });
          this.brandGroups.push(groupToAdd);
        }
        this.showAddProduct = this.brandGroups.controls.map(() => false);
        this.newProductNames = this.brandGroups.controls.map(() => '');
        this.loading = false;
      });
  }

  brandProducts(brandIndex: number) {
    return this.brandGroups.at(brandIndex).get('products') as FormArray;
  }

  onSave() {
    const selectedProductIds = this.brandGroups.controls.reduce((result, current) => {
      const productControls = (current.get('products') as FormArray).controls;
      productControls.forEach((productControl) => {
        if (productControl.get('selected').value === true) {
          result.push(productControl.get('id').value);
        }
      });
      return result;
    }, []);
    this.dialogRef.close(selectedProductIds);
  }

  filterProducts(products: Product[], searchString: string) {
    if (!searchString) return products.slice();
    searchString = searchString.toLocaleLowerCase();
    return products.filter((product) => product.name.toLocaleLowerCase().includes(searchString));
  }

  saveNewBrand() {
    if (!this.newBrandName.trim()) return;

    let newProductBrand = new ProductBrand();
    newProductBrand.name = this.newBrandName.trim();

    this.loading = true;
    this.productBrandService.addProductBrand(newProductBrand).subscribe({
      next: (brand) => {
        this.loading = false;
        this.showAddBrand = false;
        this.newBrandName = '';

        // Re-fetch or append to the brand list
        this.brandGroups.insert(
          0,
          this.formBuilder.group({
            brandId: this.formBuilder.control(brand.id),
            brandName: this.formBuilder.control({ value: brand.name, disabled: true }),
            products: this.formBuilder.array([]),
          })
        );
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = 'Could not add brand. Please try again.';
      },
    });
  }

  cancelAddBrand() {
    this.showAddBrand = false;
    this.newBrandName = '';
  }

  createProductControl(product: { name: string; disabled?: boolean }): FormGroup {
    return this.formBuilder.group({
      name: [product.name],
      selected: [true],
      disabled: [product.disabled || false],
      highlight: [false],
    });
  }

  // Add Product
  saveNewProduct(brandIdx: number, name: string) {
    name = name.trim();
    if (!name) return;

    let newProduct: Product = new Product();
    newProduct.name = name;
    const brandGroup = this.brandGroups.at(brandIdx);
    const brandIdControl = brandGroup.get('brandId'); // must exist
    newProduct.productBrandId = brandIdControl ? brandIdControl.value : 0;

    this.loading = true;
    this.productService.addProduct(newProduct).subscribe({
      next: (product) => {
        this.loading = false;
        this.showAddProduct[brandIdx] = false;

        const productArray = this.brandProducts(brandIdx);
        const newProductGroup = this.createProductControl({ name });
        productArray.push(newProductGroup);

        // Reset Add Product UI
        this.newProductNames[brandIdx] = '';
        this.showAddProduct[brandIdx] = false;

        // Highlight flash
        newProductGroup.get('highlight').setValue(true);
        setTimeout(() => newProductGroup.get('highlight').setValue(false), 1200);
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = 'Could not add product. Please try again.';
      },
    });
  }

  cancelAddProduct(brandIdx: number) {
    this.newProductNames[brandIdx] = '';
    this.showAddProduct[brandIdx] = false;
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
